import { useCallback, useEffect } from 'react';
import { FormRenderProps } from 'react-final-form';
import { Box, Grid, Stack } from '@mui/material';

import { snackbar, TextField } from '@occc-app/design-system';
import { Typography, InfoOutlinedIcon, MetricTooltip } from '@occc/ui-design-system';
import { OfferFilter } from '@occc/graphql';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { PromptAtPump } from '@occc-app/routes/CampaignComponents/PromptAtPump/PromptAtPump';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import config, { useConfig } from '@occc/config';
import {
  AwardRestrictions,
  FulfillmentChannelConstraint,
  Returnable,
} from '@occc/feat-constraints';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { OfferSelect } from '@occc-app/routes/CampaignComponents/components/OfferSelect';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';

import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';
import { ConfigureFormValues } from '../../types';
import { Redemption } from '../Redemption';
import { OfferReturnStrategy } from '../../../../dataAccessLayer/chain/types';
import { AllowedForNewMembers } from '../AllowedForNewMembers';

type ConfigureFormContentProps = {
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  dirty: boolean;
  isFormEditable: boolean;
  currentFlowType: Flow_Types;
  submitting: boolean;
  onNextStepButtonClick?: () => void;
  handleSubmit: FormRenderProps<ConfigureFormValues>['handleSubmit'];
  makeOffersFilter: (search: string) => OfferFilter;
  values: ConfigureFormValues;
  offerReturnStrategy: OfferReturnStrategy;
};

export const ConfigureFormContent = ({
  onCancelClick,
  dirty,
  isFormEditable,
  currentFlowType,
  submitting,
  onNextStepButtonClick,
  handleSubmit,
  makeOffersFilter,
  values,
  offerReturnStrategy,
}: ConfigureFormContentProps) => {
  useEffect(() => {
    if (values.isOfferReturnable) {
      snackbar.regular(
        'This chain is configured to allow returns. If a member returns this Reward, the points will be refunded to their balance.',
      );
    }
  }, [values.isOfferReturnable]);

  const isOrderAheadEnabled = useConfig('orderAheadConfig').enabled;

  const handleCancelClick = useCallback(() => {
    onCancelClick(dirty);
  }, [dirty, onCancelClick]);

  const formFooterActionButtons = isFormEditable ? (
    <CampaignComponentFooterActions
      currentFlowType={currentFlowType}
      onCancel={handleCancelClick}
      isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
    />
  ) : (
    <CampaignComponentReadOnlyFooterActions
      onCancel={handleCancelClick}
      onNextStepClick={onNextStepButtonClick}
    />
  );

  return (
    <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
      <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
        <OfferEarningMechanismConfigurationSection>
          <Box mb={2}>
            <OfferNameAndDescriptionConfiguration entityName='Reward' />
            <OfferFulfillmentPriorityField mt={2} />
          </Box>
          <Stack direction='column' spacing={2}>
            <Box>
              <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                <Typography variant='subtitle1'>Give members</Typography>
                <MetricTooltip title='Reward must be published to be selected'>
                  <InfoOutlinedIcon fontSize='small' sx={{ color: 'silver' }} />
                </MetricTooltip>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs>
                  <OfferSelect
                    name='awardVouchers.voucheredOffer'
                    label='Select Award'
                    makeOffersFilter={makeOffersFilter}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    name='awardVouchers.voucherCount'
                    label='Number of Awards'
                    fieldProps={{
                      parse: parseNumber,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                When They Spend
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <TextField
                    name='pointSpend.pointCost'
                    label='Points'
                    fieldProps={{
                      parse: parseNumber,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </OfferEarningMechanismConfigurationSection>

        <OfferConfigurationSection title='Limits'>
          <AllowedForNewMembers />
          <Redemption values={values} />
        </OfferConfigurationSection>

        <OfferConfigurationSection title='Requirements'>
          {config.features.promptAtPumpEnabled && (
            <PromptAtPump
              values={values}
              description='Ask members at the pump to purchase this offer'
            />
          )}
          <Returnable
            disabled={(['ON', 'OFF'] as OfferReturnStrategy[]).includes(offerReturnStrategy)}
          />
          <AwardRestrictions />
          {isOrderAheadEnabled && (
            <FulfillmentChannelConstraint values={values.fulfillmentChannelConstraint} />
          )}
        </OfferConfigurationSection>

        <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
          <CustomData values={values.customData} />
        </OfferConfigurationSection>
      </OfferConfigurationWizardFormLayout>
    </OfferForm>
  );
};
